import { FC, KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { createUseStyles } from 'react-jss';
import WaveSurfer from 'wavesurfer.js';
import WebAudioPlayer from 'wavesurfer.js/dist/webaudio.js';
import { IRecording } from '../../recording/RecordingState';
import { formatTime } from '../../recording/formatTime';
import { PlayIcon, PauseIcon } from '../../theme/Icons';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CareMapState } from '../../caremap/CareMapState';
import { Logger } from '../../support/Logger';
import { useIsNative } from '../../utils/useIsNative';

const SKELETON_BAR_COUNT = 70;
const KEYBOARD_SEEK_SECONDS = 5;

const useStyles = createUseStyles({
    '@keyframes waveformSkeletonPulse': {
        '0%, 100%': { opacity: 0.25 },
        '50%': { opacity: 0.7 }
    },
    skeletonBar: {
        flex: 1,
        backgroundColor: 'var(--ion-color-step-300, #b3bfc3)',
        borderRadius: 3,
        animation: '$waveformSkeletonPulse 1.2s ease-in-out infinite'
    },
    waveformContainer: {
        height: '100%',
        outline: 'none',
        '&:focus-visible': {
            boxShadow: '0 0 0 2px var(--ion-color-primary)',
            borderRadius: 4
        }
    }
});

export const RecordingPlayer: FC<{ recording: IRecording; noteId: string }> = ({ recording, noteId }) => {
    const classes = useStyles();
    const tracking = useInstance(MixPanelEventEmitter);
    const careMapState = useInstance(CareMapState);
    const logger = useInstance(Logger);
    const isNative = useIsNative();
    const careMapId = careMapState.getActiveCaremap()?.careMapId;

    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    // Fires the tracking event once per RecordingPlayer mount. Because the player
    // is conditionally rendered behind the Recording tab, tab-toggling Recording→
    // Summary→Recording produces a fresh mount and the event fires again.
    const hasTrackedPlayRef = useRef(false);

    const [isReady, setIsReady] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        // wavesurfer renders to a canvas; canvas color strings can't be CSS variables,
        // so resolve the Ionic theme values from the document at mount time. Theme
        // toggles after mount won't repaint — Innerhive doesn't have a runtime theme
        // toggle today, so this is acceptable.
        const root = getComputedStyle(document.documentElement);
        const waveColor = root.getPropertyValue('--ion-color-step-300').trim() || '#b3bfc3';
        const progressColor = root.getPropertyValue('--ion-color-primary').trim() || '#11789D';

        // Android's MediaRecorder produces non-seekable WebM (no Cues). The default
        // media-element backend throws "FFmpegDemuxer: demuxer seek failed" the moment
        // playback seeks. WebAudioPlayer plays the already-decoded PCM, so no demuxer
        // seek occurs and these recordings play on every platform.
        // Cast needed because wavesurfer types `media` as HTMLMediaElement, but its own
        // WebAudioPlayer (intended to be passed here) doesn't implement that interface
        // — drop cast if wavesurfer widens the `media` option type in a future release.
        // Own the AudioContext rather than letting WebAudioPlayer create its own, so the cleanup
        // below can close it. Each player would otherwise leave one open, and browsers allow only a
        // handful at a time, so opening a few recordings would leave later ones unable to play.
        const audioContext = new AudioContext();
        const media = new WebAudioPlayer(audioContext) as unknown as HTMLMediaElement;

        audioContextRef.current = audioContext;

        // WebKit gives a page an ambient audio session by default, which the ring/silent switch
        // mutes. It promotes that to playback for a media element but not for Web Audio, which is
        // what this player uses, so a recording is silent with the switch on until the session type
        // is asked for directly. Playing through a media element fixes the sound too, but hands iOS
        // the now-playing controls and leaves a private recording playable from a locked phone.
        //
        // Claimed here rather than on play: asking for it once, up front, keeps iOS from attaching
        // now-playing controls, where claiming it mid-playback puts the app back on the lock screen.
        // Not supported outside WebKit, where there is nothing to fix.
        if (navigator.audioSession) {
            navigator.audioSession.type = 'playback';
        }

        const ws = WaveSurfer.create({
            container: containerRef.current,
            url: recording.url,
            media,
            height: 28,
            waveColor,
            progressColor,
            cursorWidth: 0,
            barWidth: 2,
            barGap: 1,
            barRadius: 2,
            normalize: true,
            mediaControls: false,
            interact: true,
            dragToSeek: true
        });

        ws.on('ready', () => {
            setDuration(ws.getDuration());
            setIsReady(true);
        });
        ws.on('play', () => setIsPlaying(true));
        ws.on('pause', () => setIsPlaying(false));
        ws.on('finish', () => setIsPlaying(false));
        ws.on('timeupdate', t => setCurrentTime(t));
        ws.on('error', err => {
            logger.error(err, { message: 'Recording player failed to load', recordingUrl: recording.url });
            setLoadFailed(true);
        });

        wavesurferRef.current = ws;

        return () => {
            // destroy() tears down wavesurfer's own machinery but never touches the media, and
            // WebAudioPlayer has no destroy of its own. Its AudioBufferSourceNode is wired straight
            // to the speakers rather than to anything in the DOM, so without an explicit pause the
            // recording keeps playing after the note is closed, with nothing left holding a
            // reference to stop it.
            ws.pause();
            ws.destroy();
            // Reported rather than dropped: a context that fails to close is the very leak this
            // cleanup exists to prevent, and an unhandled rejection would look exactly like success.
            audioContext.close().catch(err => logger.error(err, { message: 'Failed to close the recording player audio context' }));
            wavesurferRef.current = null;
            audioContextRef.current = null;
        };
    }, [recording.url, logger]);

    // Backgrounding the app is the user leaving the recording just as much as closing the note is,
    // so it pauses too. Without this it would keep playing: this player claims a playback audio
    // session when it mounts (see above, so a recording is audible through the silent switch) and the app
    // declares the audio background mode for recording, which together let playback continue once
    // the app is out of sight. There are no lock-screen controls, so that would also be audio the
    // user cannot stop without reopening the app. Native only: a hidden browser tab is expected to
    // keep playing, and pausing there would be the surprising behaviour.
    useEffect(() => {
        if (!isNative) {
            return;
        }

        const listener = App.addListener('appStateChange', ({ isActive }) => {
            if (!isActive) {
                wavesurferRef.current?.pause();
            }
        });

        return () => {
            // this player is remounted every time the Recording tab is opened, so a listener left
            // behind would pile up and fire against a destroyed wavesurfer
            listener.then(l => l.remove()).catch(err => logger.error(err, { message: 'Failed to remove the recording player app state listener' }));
        };
    }, [isNative, logger]);

    // Another app taking the audio session (music, a call) leaves this one interrupted and its
    // context suspended, and a suspended context produces no sound however many times play is
    // pressed — the button flips to pause and nothing happens. Resuming it here recovers that.
    // Deliberately does NOT re-assert the session type: doing that mid-playback is what puts the
    // app back on the lock screen (see where it is claimed, on mount).
    const resumeAudioContext = async () => {
        const audioContext = audioContextRef.current;
        if (audioContext && audioContext.state !== 'running') {
            await audioContext.resume();
        }
    };

    // playPause() is async and can reject (the source node refusing to start), so both entry points
    // funnel through here rather than one of them leaving an unhandled rejection. Note an autoplay
    // refusal does NOT arrive this way: it leaves the context suspended, which resumeAudioContext
    // handles, and a decode failure surfaces through wavesurfer's own error event.
    const toggle = () => {
        handleToggle().catch(err => logger.error(err, { message: 'Failed to toggle recording playback' }));
    };

    const handleToggle = async () => {
        const ws = wavesurferRef.current;
        if (!ws) {
            return;
        }
        const wasPlaying = ws.isPlaying();
        if (!wasPlaying) {
            try {
                await resumeAudioContext();
            } catch (err: any) {
                // still try to play: the context is usually already running, so this is often
                // recoverable. Reported because the alternative symptom is a play button that
                // flips to pause and produces no sound, which is impossible to diagnose from here.
                logger.error(err, { message: 'Failed to resume the recording player audio context' });
            }
        }
        // re-read after the await: the note or tab can close while a suspended context resumes,
        // and the cleanup will have destroyed this instance out from under us by then.
        if (wavesurferRef.current !== ws) {
            return;
        }

        await ws.playPause();
        if (!wasPlaying && !hasTrackedPlayRef.current) {
            hasTrackedPlayRef.current = true;
            tracking.track('Recording Played From Note', () => ({
                noteId,
                careMapId,
                'Duration Seconds': recording.msDuration ? Math.round(recording.msDuration / 1000) : null
            }));
        }
    };

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        const ws = wavesurferRef.current;
        if (!ws || !isReady) {
            return;
        }
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            ws.setTime(Math.min(ws.getDuration(), ws.getCurrentTime() + KEYBOARD_SEEK_SECONDS));
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            ws.setTime(Math.max(0, ws.getCurrentTime() - KEYBOARD_SEEK_SECONDS));
        } else if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            toggle();
        }
    };

    const totalSeconds = duration || recording.msDuration / 1000;
    const displayTime = isPlaying
        ? formatTime(currentTime)
        : formatTime(totalSeconds);

    return (
        <div
            data-id="recording-player"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                border: '1px solid var(--ion-color-light)',
                borderRadius: 12,
                margin: '12px 0'
            }}
        >
            <IonButton
                fill="clear"
                size="small"
                onClick={toggle}
                data-id={isPlaying ? 'recording-player-pause' : 'recording-player-play'}
            >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IonButton>

            <div style={{ flex: 1, minWidth: 0, height: 28, position: 'relative' }}>
                <div
                    ref={containerRef}
                    data-id="recording-player-waveform"
                    role="slider"
                    tabIndex={0}
                    aria-label="Recording playback position"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(totalSeconds)}
                    aria-valuenow={Math.round(currentTime)}
                    aria-valuetext={`${formatTime(currentTime)} of ${formatTime(totalSeconds)}`}
                    onKeyDown={handleKeyDown}
                    className={classes.waveformContainer}
                    style={{ opacity: isReady ? 1 : 0 }}
                />
                {!isReady && !loadFailed && (
                    <div
                        aria-hidden="true"
                        data-id="recording-player-waveform-skeleton"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pointerEvents: 'none'
                        }}
                    >
                        {Array.from({ length: SKELETON_BAR_COUNT }).map((_, i) => {
                            const heightPct = 30 + ((i * 37) % 50);
                            const delayMs = (i % 8) * 80;
                            return (
                                <div
                                    key={i}
                                    className={classes.skeletonBar}
                                    style={{
                                        height: `${heightPct}%`,
                                        animationDelay: `${delayMs}ms`
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
                {loadFailed && (
                    <div
                        data-id="recording-player-load-failed"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            color: 'var(--ion-color-step-600, #666666)'
                        }}
                    >
                        Couldn’t load audio
                    </div>
                )}
            </div>

            <span
                style={{
                    fontSize: 12,
                    color: 'var(--ion-color-step-600, #666666)',
                    minWidth: 44,
                    textAlign: 'right'
                }}
            >
                {displayTime}
            </span>
        </div>
    );
};
